import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";

import {
  IAllVehicleResponse,
  ISingleVehicleResponse,
  IVehicle,
  VehicleDeletedReason,
} from "../models/Vehicle";
import { DefaultResponseWithData } from "../models/system";

export interface ISoftDeleteVehicleRequest {
  vehicleId: string;
  deletedReason: VehicleDeletedReason;
  deletedNote?: string;
}

export default function useVehiclesMutations() {
  const { get, post, put, patch, del } = useApiHelper();
  const queryClient = useQueryClient();

  const vrmLookup = useMutation(
    (vrm: string) => get<ISingleVehicleResponse>(`/vehicle/lookup/${vrm}`),
    {
      onSuccess: () => {

      },
    }
  );

  // Lookup that never creates. The `lookup` endpoint provisions a vehicle from the
  // UKVD data for any VRM it doesn't already hold, so a mistyped reg silently adds a
  // vehicle to the system. Callers that only want to reach a vehicle that already
  // exists use this instead, and get `null` back when there's no match.
  const vrmLookupExisting = useMutation(async (vrm: string) => {
    try {
      return await get<ISingleVehicleResponse>(`/vehicle/lookup/${vrm}?createIfMissing=false`);
    } catch (error: any) {
      if (error?.message?.includes("HTTP Error: 404")) return null;
      throw error;
    }
  });

  // Lookup for the registration search boxes: returns our vehicle when we hold one and the
  // UKVD details when we don't, without writing either. A vehicle the `lookup` endpoint
  // creates has no lead, and a document can only be filed against a lead — so every reg
  // someone typed into a filter used to leave a vehicle that could be triaged and given a
  // yard location but could never hold a photograph.
  //
  // The vehicle handed back for a reg we don't hold has no id. Read its details; never key
  // a query, job or link off it — use `vrmLookup` where a real record is needed.
  const vrmLookupData = useMutation(async (vrm: string) => {
    try {
      return await get<ISingleVehicleResponse>(`/vehicle/lookup/${vrm}?mode=DataOnly`);
    } catch (error: any) {
      if (error?.message?.includes("HTTP Error: 404")) return null;
      throw error;
    }
  });

  const createMisc = useMutation(
    (vehicle: any) => post<ISingleVehicleResponse>("/vehicle/misc", vehicle),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead"]);
        queryClient.setQueryData(["vehicles", data.data.id], data);
      },
      onError: (error) => {
        console.error("Error creating miscellaneous vehicle:", error);
      },
    }
  );

  // Paged tables embed a copy of the vehicle on their rows (parts hold `vehicle`;
  // recently listed nests the part under `partData`). Cells deriving from that copy —
  // the eBay snapshot's missing fields, KTYPE warnings — render stale unless the cached
  // rows are rebuilt with fresh identities, and invalidating ["vehicles"] doesn't reach them.
  const syncPagedTableVehicles = (vehicle: IVehicle) => {
    const merged = (embedded: object) => ({
      ...embedded,
      // Only keys the submitted body actually carries, so row projections keep
      // any joined fields the PUT payload doesn't know about.
      ...Object.fromEntries(Object.entries(vehicle).filter(([, value]) => value !== undefined)),
    });

    const patchRow = (row: any) => {
      if (row?.vehicle?.id === vehicle.id) return { ...row, vehicle: merged(row.vehicle) };

      if (row?.partData?.vehicle?.id === vehicle.id) {
        return { ...row, partData: { ...row.partData, vehicle: merged(row.partData.vehicle) } };
      }

      return row;
    };

    queryClient.setQueriesData(["pagedTable"], (oldData: any) => {
      const rows = oldData?.data?.rows;
      if (!Array.isArray(rows)) return oldData;

      return { ...oldData, data: { ...oldData.data, rows: rows.map(patchRow) } };
    });
  };

  const update = useMutation(
    (vehicle: IVehicle) => put(`/vehicle/${vehicle.id}`, vehicle),
    {
      onMutate: (vehicle: IVehicle) => {
        queryClient.cancelQueries(["vehicles"]);
        const previousVehicle = queryClient.getQueryData<DefaultResponseWithData<IVehicle>>(["vehicles", vehicle.id]);

        queryClient.setQueryData(["vehicles", vehicle.id], {
          success: true,
          message: "Optimistically updated",
          data: vehicle,
        });

        return { previousVehicle, vehicle };
      },
      onSuccess: (_data, vehicle) => {
        syncPagedTableVehicles(vehicle);
      },
      onError: (err, vehicle, context) => {
        queryClient.setQueryData(
          ['vehicles', context?.vehicle.id],
          context?.previousVehicle,
        )
      },
      onSettled: (_data, _error, _vehicle) => {
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead"]);
        queryClient.invalidateQueries(["job"]);
      },
    }
  );

  const updateDetails = useMutation(
    (data: { vehicleId: string; details: Partial<IVehicle> }) => put(`/vehicle/${data.vehicleId}/details`, data.details),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead"]);
        // Details can change the purchase VAT flag, which drives parts' resolved treatment.
        queryClient.invalidateQueries(["part-vat-resolution"]);
        queryClient.invalidateQueries(["omniSearchVat"]);
      },
    }
  );

  const bulkUpdate = useMutation(
    (body: any) => post(`/vehicle/bulk`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["vehicles"]);
      },
    }
  );

  const vrmRefresh = useMutation(
    (vehicle: IVehicle) => post(`/vehicle/${vehicle.id}/refresh`, { "vrm": vehicle.vrm }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["lead"]);
      },
    }
  );

  const enginesWorldwideRefresh = useMutation(
    (vehicleId: string) => post<undefined, ISingleVehicleResponse>(`/vehicle/${vehicleId}/engines-worldwide/refresh`, undefined),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead"]);
        queryClient.setQueryData(["vehicles", data.data.id], data);
      },
    }
  );

  const updateNotes = useMutation(
    (data: { vehicleId: string; notes: string | null }) =>
      patch(`/note/vehicle`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead"]);
      },
    }
  );

  const bulkMarkCollected = useMutation(
    (body: any) => post(`/vehicle/bulk/mark/collected`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["vehiclePagedTable"]);
      },
    }
  );

  const patchVehicle = useMutation(
    (data: { vehicleId: string; patch: object }) => patch(`/vehicle/${data.vehicleId}`, data.patch),
    {
      onSettled: (_data, _error, variables) => {
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["vehicles", variables.vehicleId, "workflow"]);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead"]);
        queryClient.invalidateQueries(["planner-awaiting"]);
        queryClient.invalidateQueries(["planner-allocated"]);
        queryClient.invalidateQueries(["production-dashboard-overview"]);
        queryClient.invalidateQueries(["pagedTable", "/vehicle"]);
      },
    }
  );

  // The reason and note are mandatory server-side, so they travel in the DELETE body rather than
  // the mutation taking a bare id.
  const softDelete = useMutation(
    ({ vehicleId, deletedReason, deletedNote }: ISoftDeleteVehicleRequest) =>
      del(`/vehicle/${vehicleId}`, { deletedReason, deletedNote }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["vehiclePagedTable"]);
      },
    }
  );

  return {
    vrmLookup,
    vrmLookupExisting,
    vrmLookupData,
    createMisc,
    update,
    updateDetails,
    bulkUpdate,
    vrmRefresh,
    enginesWorldwideRefresh,
    updateNotes,
    bulkMarkCollected,
    patchVehicle,
    softDelete,
  };
}
